import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { format } from 'date-fns';

// Normalize any date value to a "YYYY-MM-DD" string safely.
// Accepts: Date objects, ISO strings ("2026-05-01T..."), or bare date strings ("2026-05-01").
// Using substring(0,10) on strings avoids the UTC-parse timezone shift that format() causes.
const toDateStr = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.substring(0, 10);
  return format(d, 'yyyy-MM-dd');
};

export const useDynamiaERP = () => {
  const { activeEmployee } = useAuth();

  const [employeeSalesData, setEmployeeSalesData] = useState(null);
  const [unifiedSalesData,  setUnifiedSalesData]  = useState(null);
  const [salesMetadata,     setSalesMetadata]     = useState(null);
  const [channelBreakdown,  setChannelBreakdown]  = useState(null);
  const [loading,           setLoading]           = useState(false);
  const [error,             setError]             = useState('');
  const [authenticated,     setAuthenticated]     = useState(true);

  useEffect(() => {
    console.log('✅ useDynamiaERP inicializado - usando Firebase Functions');
  }, []);

  // ─── Employee dashboard ───────────────────────────────────────────────────
  // Fetches sales for the active employee (called from EmployeeDashboardPage).
  const fetchReportForDateRange = useCallback(async (startDate, endDate) => {
    if (!activeEmployee) {
      console.warn('⚠️ No hay empleado activo');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const start = toDateStr(startDate);
      const end   = toDateStr(endDate);

      console.log(`🔍 Obteniendo ventas para ${activeEmployee.name} (${start} → ${end})`);

      const callFn = httpsCallable(functions, 'fetchDynamiaSalesData');
      const result = await callFn({ startDate: start, endDate: end });

      const processedData = result.data?.data;

      if (!processedData?.sellers) {
        console.warn('⚠️ Respuesta sin datos de vendedores:', result.data);
        setEmployeeSalesData({ sales: 0, orders: 0, commission: 0, commissionRate: 1, orderDetails: [] });
        return;
      }

      // Case-insensitive lookup so name format differences don't break it
      const name = activeEmployee.name;
      const employeeData = processedData.sellers[name] ||
        Object.entries(processedData.sellers).find(([k]) =>
          k.toLowerCase().trim() === name.toLowerCase().trim()
        )?.[1];

      if (employeeData) {
        setEmployeeSalesData({
          sales:          employeeData.sales,
          orders:         employeeData.orders,
          commission:     employeeData.commission,
          commissionRate: employeeData.commissionRate,
          orderDetails:   employeeData.orderDetails || [],
        });
        console.log(`✅ Datos de ventas para ${name}:`, employeeData);
      } else {
        setEmployeeSalesData({ sales: 0, orders: 0, commission: 0, commissionRate: 1, orderDetails: [] });
        console.log(`ℹ️ Sin ventas para ${name} en ${start}–${end}`);
      }

      setSalesMetadata({
        dateRange:   { startDate: start, endDate: end },
        totalOrders: processedData.totalOrders,
        source:      'DynamiaERP',
      });
    } catch (err) {
      console.error('❌ Error al llamar a DynamiaERP:', err);
      setError('No se pudo obtener el reporte de ventas.');
      setEmployeeSalesData(null);
      setSalesMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [activeEmployee]);

  // ─── Admin / unified reports ──────────────────────────────────────────────
  // All caching is handled server-side (Cloud Function v5 cache).
  // No client-side RTDB reads — Firebase rules block them and they're not needed.
  const fetchUnifiedReport = useCallback(async (startDate, endDate, options = {}) => {
    setLoading(true);
    setError('');

    const start = toDateStr(startDate);
    const end   = toDateStr(endDate);

    console.log(`🔄 Obteniendo datos actuales vía Firebase Functions: ${start} - ${end}`);

    try {
      const callFn = httpsCallable(functions, 'fetchDynamiaSalesData', { timeout: 540000 });
      const result = await callFn({ startDate: start, endDate: end });

      console.log(`📊 Respuesta de Firebase Function:`, result.data);
      const processedData = result.data?.data;

      if (!processedData) {
        throw new Error('Respuesta inválida del servidor');
      }

      setUnifiedSalesData(processedData);
      setSalesMetadata({
        dateRange:   { startDate: start, endDate: end },
        totalOrders: processedData.totalOrders,
        source:      'DynamiaERP',
        generatedAt: new Date().toISOString(),
      });
      setChannelBreakdown({
        pos: {
          orders:      processedData.totalOrders,
          sales:       processedData.totalSales,
          commissions: processedData.totalCommissions,
        },
        web: { orders: 0, sales: 0, commissions: 0 },
      });

      console.log('✅ Reporte unificado generado exitosamente');
      console.log(`   Vendedores: ${Object.keys(processedData.sellers).length}`);
      console.log(`   Ventas totales: $${processedData.totalSales}`);
      console.log(`   Comisiones totales: $${processedData.totalCommissions}`);

      return { success: true, data: processedData };
    } catch (err) {
      console.error('❌ Error al obtener reporte unificado DynamiaERP:', err);
      setError('No se pudo obtener el reporte unificado.');
      setUnifiedSalesData(null);
      setSalesMetadata(null);
      return { success: false, error: err.message || 'Error desconocido' };
    } finally {
      setLoading(false);
    }
  }, []);

  // ─── Derived getters (kept for usePayroll compatibility) ─────────────────
  const getChannelStatistics = useCallback(() => {
    if (!channelBreakdown) return null;
    return {
      pos: { label: 'Punto de Venta (DynamiaERP)', ...channelBreakdown.pos, percentage: 100 },
      web: { label: 'Tienda Online',               ...channelBreakdown.web, percentage: 0  },
    };
  }, [channelBreakdown]);

  const getPeriodStatistics = useCallback(() => {
    if (!unifiedSalesData) return null;
    return {
      legacy: { label: 'Ventas Legacy', count: 0, percentage: 0 },
      modern: { label: 'Ventas DynamiaERP', count: unifiedSalesData.totalOrders, percentage: 100 },
    };
  }, [unifiedSalesData]);

  const findSellerByName = useCallback((sellerName) => {
    if (!unifiedSalesData?.sellers) return null;
    return unifiedSalesData.sellers[sellerName] || null;
  }, [unifiedSalesData]);

  const getExecutiveSummary = useCallback(() => {
    if (!unifiedSalesData) return null;
    const topSellers = Object.entries(unifiedSalesData.sellers)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 5);
    return {
      totalSales:          unifiedSalesData.totalSales,
      totalOrders:         unifiedSalesData.totalOrders,
      totalCommissions:    unifiedSalesData.totalCommissions,
      averageOrderValue:   unifiedSalesData.totalOrders > 0
        ? (unifiedSalesData.totalSales / unifiedSalesData.totalOrders).toFixed(2)
        : 0,
      topSellers,
      commissionSellers:    Object.values(unifiedSalesData.sellers).filter(s => s.commissionRate > 0).length,
      channelDistribution:  getChannelStatistics(),
      periodDistribution:   getPeriodStatistics(),
    };
  }, [unifiedSalesData, getChannelStatistics, getPeriodStatistics]);

  const clearData = useCallback(() => {
    setEmployeeSalesData(null);
    setUnifiedSalesData(null);
    setSalesMetadata(null);
    setChannelBreakdown(null);
    setError('');
    console.log('🧹 Datos de DynamiaERP limpiados');
  }, []);

  return {
    // State
    employeeSalesData,
    unifiedSalesData,
    salesMetadata,
    channelBreakdown,
    loading,
    error,
    authenticated,

    // Functions
    fetchReportForDateRange,
    fetchUnifiedReport,

    // Derived
    getChannelStatistics,
    getPeriodStatistics,
    getExecutiveSummary,
    findSellerByName,

    // Utilities
    clearData,
  };
};

export default useDynamiaERP;
